import React from "react";
import { Table } from "react-bootstrap";
import {
  Button,
  Input,
  Select,
  Tooltip,
  Skeleton,
} from "antd";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import {
  getPesticideCustomerData,
  updatePesticideCountries,
} from "../../../../actions/sendReports";
import Loading from "../../../../assets/images/loading.gif";
import { getSearchOptions } from "../../../../actions/batchReports";
import "./PesticideCustomers.css";

class PesticideCustomers extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      columns: [
        { field: "customer_id", name: "Customer ID" },
        { field: "company", name: "Customer Name" },
        { field: "country_list", name: "Countries" },
      ],
      dataSource: [],
      loading: true,
      postLoading: false,
      options: [],
      countryList: [],
      randomKey: 0,
      curSearchValue: "",
      apiController: null,
    };
    this.isUnmounted = false;
  }

  componentDidMount() {
    this.getReadyFiles();
    this.setSearchOptions();
  }

  componentWillUnmount() {
    this.isUnmounted = true; // Set the flag to indicate unmounting
    this.abortPrevApiCall(null);
  }

  /**
   * Use the AbortController to cancel the last producttemplates/ call if the last call was still in progress.
   * @param {*} controller
   */
  abortPrevApiCall = (controller) => {
    const { apiController } = this.state;
    if (apiController) {
      apiController.abort();
    }
    this.setState({ apiController: controller });
  }

  /**
   * Make an api call to get all ready pesticide files
   */
  getReadyFiles = async () => {
    const { postLoading } = this.state;
    this.setState({ loading: !postLoading }, async () => {
      const { curSearchValue } = this.state;
      const controller = new AbortController();
      this.abortPrevApiCall(controller);
      const response = await getPesticideCustomerData(curSearchValue, controller.signal);
      if (this.isUnmounted) {
        return;
      }
      if (postLoading) {
        toast.success("Countries updated successfully");
      }
      if (response.success) {
        const result = response.result;
        const newDs = result.map((item) => ({ ...item, disableEdit: true, countryFormList: [...item.country_list] }));
        this.setState({
          dataSource: newDs,
          loading: false,
          postLoading: false,
          countryList: response.all_countries.map((item) => ({ value: item })),
        });
      } else if (!response.canceled) {
        this.setState({
          loading: false,
          postLoading: false,
          dataSource: [],
          countryList: [],
        });
        toast.error(response.message);
      }
    });
  };

  /**
   * Set up the default country list
   */
  setSearchOptions = async () => {
    const response = await getSearchOptions();
    const tempMap = {};
    if (response && response.success) {
      const options = [];
      response.list.forEach((item) => {
        if (!tempMap[item.customer_id]) {
          tempMap[item.customer_id] = item.customer_id;
          options.push({ value: item.customer_id, company: item.company });
        }
      });
      this.setState({
        options,
      });
    }
  };

  /**
   * Make an api call to add a new Customer with selected countries
   * @returns
   */
  handleAddNewCustomer = () => {
    const { dataSource, loading } = this.state;
    if (loading) {
      toast.warning("Data is loading, please wait");
      return;
    }
    const newRandomKey = uuidv4();
    // if a new row already existed
    if (dataSource.length > 0 && dataSource[0].customer_id === "") {
      return;
    }
    const newDs = [];
    newDs.push({
      customer_id: "",
      company: "",
      country_list: [],
      countryFormList: [],
      disableEdit: false,
      newRow: true,
    });
    dataSource.forEach(
      (item) => {
        item.disableEdit = true;
        newDs.push({ ...item });
      },
    );
    this.setState({ dataSource: newDs, randomKey: newRandomKey });
  }

  /**
   * Update the selected customer id and company name when adding a new customer on the table
   * @param {*} option
   */
  onCustomerSelect = (option) => {
    const { dataSource } = this.state;
    dataSource[0].customer_id = option.value;
    dataSource[0].company = option.company;
    this.setState({ dataSource });
  };

  /**
   * Enable edit the country list for current row
   * @param {*} targetIdx
   */
  handleRowDoubleClick = (targetIdx) => {
    const { dataSource } = this.state;
    const isAddingCustomer = dataSource[0].newRow;
    if (!(isAddingCustomer && targetIdx === 0)) {
      let ds = dataSource;
      if (isAddingCustomer) {
        ds = dataSource.slice(1);
      }
      dataSource.forEach((item, index) => {
        if (index === targetIdx) {
          item.disableEdit = false;
        } else {
          item.disableEdit = true;
        }
      });
      const newRandomKey = uuidv4(); // force rerender country Select dropdown
      dataSource[targetIdx].countryFormList = [...dataSource[targetIdx].country_list];
      this.setState({ dataSource: ds, randomKey: newRandomKey });
    }
  };

  /**
   * Make an api call to add a new customer or update a current customer
   * @param {*} idx
   */
  handleUpdateCustomer = async (idx) => {
    this.setState({ postLoading: true }, async () => {
      const { dataSource } = this.state;
      if (dataSource[idx].customer_id === "") {
        toast.warning("Please enter a valid Customer ID");
        this.setState({ postLoading: false });
        return;
      }
      if (dataSource[idx].country_list.length === 0 && dataSource[idx].newRow && dataSource[idx].countryFormList.length === 0) {
        toast.warning("Please select one or more countries");
        this.setState({ postLoading: false });
        return;
      }
      const payload = {
        customer_id: dataSource[idx].customer_id,
        country_list: dataSource[idx].countryFormList,
        action: dataSource[idx].newRow ? "new" : "edit",
      };
      const response = await updatePesticideCountries(payload);
      if (response.success) {
        this.getReadyFiles();
      } else {
        toast.error(response.message);
        this.setState({ postLoading: false });
      }
    });
  };

  /**
   * Disable all edit for the country column
   * @param {*} idx
   */
  handleCancel = (idx) => {
    const { dataSource } = this.state;

    if (idx === 0 && dataSource[idx].newRow) {
      const newDs = [];
      dataSource.forEach((item) => {
        if (!item.newRow) {
          newDs.push({ ...item, countryFormList: [...item.country_list] });
        }
      });
      this.setState({ dataSource: newDs });
    } else {
      dataSource[idx].disableEdit = true;
      dataSource[idx].countryFormList = [...dataSource[idx].country_list];
      this.setState({ dataSource });
    }
  }

  /**
   * Make an api call to search the pesticides customers based on customer id or company name
   * @param {*} e
   * @param {*} clearSearch
   */
  handleSearchEnter = async (e, clearSearch = false) => {
    this.setState({ loading: true }, async () => {
      const controller = new AbortController();
      this.abortPrevApiCall(controller);
      const response = await getPesticideCustomerData(clearSearch ? "" : e.target.value.trim(), controller.signal);
      if (response.success) {
        const result = response.result;
        const dataSource = result.map((item) => ({ ...item, disableEdit: true, countryFormList: [...item.country_list] }));
        this.setState({
          dataSource,
          curSearchValue: clearSearch ? "" : e.target.value,
        });
      } else if (!response.canceled) {
        toast.error(response.message);
      }
      this.setState({ loading: response.canceled });
    });
  };

  handleSearchOnchange = (e) => {
    if (e.target.value === "") {
      this.handleSearchEnter(null, true);
    }
  };

  /**
   * action: 0 -> add, 1 -> remove, 2 -> clear
   * @param {*} action
   * @param {*} idx
   * @param {*} text
   * @returns
   */
  handleOptionSelect = (action, text, index) => {
    const { dataSource } = this.state;
    if (action === 2) {
      dataSource[index].countryFormList = [];
      this.setState({ dataSource });
      return;
    }
    if (action === 0) {
      if (!this.isLetter(text) || (text.length > 3 || text.length === 1) || dataSource[index].countryFormList.includes(text.toUpperCase())) {
        dataSource[index].countryStatus = "";
        this.setState({ dataSource, randomKey: uuidv4() });
        return;
      }
      dataSource[index].countryFormList.push(text.toUpperCase());
    } else {
      const cIdx = dataSource[index].countryFormList.indexOf(text);
      dataSource[index].countryFormList.splice(cIdx, 1);
    }

    this.setState({ dataSource, randomKey: uuidv4() });
  };

  /**
   * If the current search does not match the requirements, then make the Select in red border
   * 1. Letter only
   * 2. The input needs to be two or three letter
   * @param {*} text
   * @param {*} index
   */
  handleCountryOnSearch = (text, index) => {
    const { dataSource } = this.state;
    if (text !== "" && (!this.isLetter(text) || text.length > 3)) {
      dataSource[index].countryStatus = "error";
    } else {
      dataSource[index].countryStatus = "";
    }
    this.setState({ dataSource });
  }

  isLetter = (value) => {
    const letterPattern = /^[A-Za-z]+$/;
    if (!letterPattern.test(value)) {
      // If the input contains non-letter characters, prevent the change
      return false;
    }
    return true;
  }

  addTableSkeleton = (i) => {
    const keys = ["CustomerId", "CustomerName", "Countries"];
    return (
      <tr key={`PesticideCustomers-tr-${i}`}>
        {[...Array(3)].map((_, j) => (
          <td key={`PesticideCustomers-${i}-${j}`} className={`PesticideCustomers__Skeleton__${keys[j]}__Td`}>
            <Skeleton.Input className="PesticideCustomers__Skeleton__Sample" active />
          </td>
        ))}
      </tr>
    );
  }

  render() {
    const {
      columns,
      dataSource,
      loading,
      postLoading,
      options,
      countryList,
      randomKey,
    } = this.state;

    return (
      <div>
        <div className="PesticideCustomers__TopBar">
          <Input.Search
            className="PesticideCustomers__FilterSearch"
            placeholder="Search by Customer ID, Customer Name"
            onPressEnter={(e) => this.handleSearchEnter(e)}
            onChange={(e) => this.handleSearchOnchange(e)}
            onSearch={(value) => this.handleSearchEnter({ target: { value } })}
          />
          <Button
            type="button"
            onClick={() => this.handleAddNewCustomer()}
            className="PesticideCustomers__AddBtn"
            disabled={loading}
          >
            Add New Customer
          </Button>
        </div>
        <Table bordered={!loading} hover={!loading} striped={!loading}>
          <thead>
            <tr className="PesticideCustomers__Heading">
              {columns.map(({ field, name }) => (
                <td key={field}>{name}</td>
              ))}
            </tr>
          </thead>
          <tbody className="PesticideCustomers__Tablebody">
            {!loading && dataSource.map((item, index) => (
              <Tooltip
                placement="topRight"
                title={item.newRow || !item.disableEdit ? "" : "Double Click to Edit Countries"}
                overlayClassName="PesticideCustomers__Tooltip"
                key={index + randomKey}
              >
                <tr
                  key={index}
                  onDoubleClick={() => this.handleRowDoubleClick(index)}
                >
                  {columns.map(({ field }, i) => {
                    if (field === "country_list") {
                      if (item.disableEdit) {
                        return (
                          <td
                            className="PesticideCustomers__Row PesticideCustomers__Row--fixed-width"
                            key={i}
                          >
                            {item[field].join(", ")}
                          </td>
                        );
                      }
                      return (
                        <td className="PesticideCustomers__Row PesticideCustomers__Row--fixed-width" key={i}>
                          <div
                            className="PesticideCustomers__Countries"
                            key={i}
                          >
                            <Tooltip
                              placement="topLeft"
                              title="Only two or three letters are allowed."
                              overlayClassName="PesticideCustomers__Country__Tooltip"
                              open={item.countryStatus === "error"}
                              key={index}
                            >
                              <Select
                                mode="tags"
                                allowClear
                                placeholder="Select Countries"
                                value={item.countryFormList}
                                options={countryList}
                                status={item.countryStatus}
                                onSelect={(text) => this.handleOptionSelect(0, text, index)}
                                onDeselect={(text) => this.handleOptionSelect(1, text, index)}
                                onClear={() => this.handleOptionSelect(2, index, index)}
                                onSearch={(text) => this.handleCountryOnSearch(text, index)}
                                className="PesticideCustomers__Select"
                              />
                            </Tooltip>
                            <div className="PesticideCustomers__CountryBtnDiv">
                              <Button
                                type="primary"
                                className="PesticideCustomers__CountryBtn"
                                onClick={() => this.handleUpdateCustomer(index)}
                              >
                                Confirm
                              </Button>
                              <Button
                                className="PesticideCustomers__CountryBtn"
                                onClick={() => this.handleCancel(index)}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        </td>
                      );
                    }
                    if (field === "customer_id" && item.newRow) {
                      return (
                        <td className="PesticideCustomers__Row" key={i}>
                          <Select
                            showSearch
                            className="PesticideCustomers__Autocomplete"
                            placeholder="Select Customer ID"
                            optionFilterProp="children"
                            defaultValue={item.customer_id}
                            filterOption={(inputValue, option) => option.value?.toLowerCase().indexOf(inputValue?.toLowerCase()) !== -1}
                            options={options}
                            onSelect={(_, option) => this.onCustomerSelect(option)}
                          />
                        </td>
                      );
                    }
                    return (
                      <td className="PesticideCustomers__Row" key={i}>
                        {item[field]}
                      </td>
                    );
                  })}
                </tr>
              </Tooltip>
            ))}
            {loading
              && [...Array(6)].map((_, index) => this.addTableSkeleton(index + 1))}
          </tbody>
        </Table>
        {postLoading && (
          <div className="CommonLoading">
            <img src={Loading} alt="" />
          </div>
        )}
      </div>
    );
  }
}
export default PesticideCustomers;
